// Pure, unit-testable detection rules for the admin-only "N security events
// to review" surface (src/services/securityEvents.js). Split out on
// 2026-09-25 (card-tap-to-Activity work) purely to keep securityEvents.js
// under the 500-line cap — every function here is re-exported from
// securityEvents.js, so existing imports (`from './securityEvents'`) keep
// working unchanged.
//
// Rule shape: { id, kind, severity: 'high'|'medium', headline: {id, values},
// lastSeen, activity: { entityType, eventType } }. `activity` is the exact
// (entity_type, event_type) pair History → Activity's filter needs to show
// ONLY this event's kind (no ids/emails/names in the URL — RD-NAV-002) — it
// mirrors the same pair securityEvents.js's `fetchAuditEvents` queried it
// with, so the two can never drift apart.

const HOUR_MS = 60 * 60 * 1000;

const MASS_VOID_WINDOW_MS = HOUR_MS;
const MASS_VOID_MIN_COUNT = 3;
const LOGIN_FAILED_WINDOW_MS = HOUR_MS;
const LOGIN_FAILED_MIN_COUNT = 5;
const BULK_CATALOG_DELETE_MIN = 10;
const BULK_CATALOG_DELETE_WINDOW_MS = HOUR_MS;

function getCreatedAtMs(row) {
  const t = new Date(row?.created_at).getTime();
  return Number.isFinite(t) ? t : NaN;
}

function groupBy(rows, keyFn) {
  const map = new Map();
  (rows || []).forEach((row) => {
    const key = keyFn(row);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  });
  return map;
}

/**
 * Sliding-window burst detector — PURE, unit-testable without a DB.
 *
 * Does ANY window of `windowMs` contain at least `minCount` of the given
 * rows? Rows need not be pre-sorted. Returns the EARLIEST qualifying burst's
 * rows (scanning oldest → newest, classic two-pointer sliding window), or
 * null if no window ever reaches `minCount`.
 *
 * Example: 3 events at 0/50/90 min apart pairwise is NOT what matters — what
 * matters is span(oldest, newest) within the window. 3 voids at 0/20/50min
 * (span 50 ≤ 60) qualifies; 3 voids at 0/70/140min (span 140 > 60, and no
 * sub-run of 3 has a smaller span) does not.
 *
 * @param {Array} rows
 * @param {{ windowMs: number, minCount: number, getTime: (row) => number }} opts
 * @returns {Array|null}
 */
export function findSlidingWindowBurst(rows, { windowMs, minCount, getTime }) {
  const sorted = (rows || [])
    .map((row) => ({ row, t: getTime(row) }))
    .filter(({ t }) => Number.isFinite(t))
    .sort((a, b) => a.t - b.t);
  if (sorted.length < minCount) return null;

  let start = 0;
  for (let end = 0; end < sorted.length; end += 1) {
    while (sorted[end].t - sorted[start].t > windowMs) start += 1;
    if (end - start + 1 >= minCount) {
      return sorted.slice(start, end + 1).map(({ row }) => row);
    }
  }
  return null;
}

/**
 * Every non-overlapping qualifying burst, not just the first — an ongoing
 * pattern (3 voids at 0/20/50min, THEN 3 more at 180/200/230min) is two
 * separate incidents, not one that silently swallows the second. Finds the
 * earliest burst via `findSlidingWindowBurst`, then restarts scanning
 * strictly AFTER that burst's own newest event, so no event is ever claimed
 * by two bursts and a long-running actor/email can produce many alerts.
 *
 * @param {Array} rows
 * @param {{ windowMs: number, minCount: number, getTime: (row) => number }} opts
 * @returns {Array<Array>} zero or more bursts, each an array of rows sorted
 *   oldest → newest, in chronological order of occurrence.
 */
export function findAllSlidingWindowBursts(rows, opts) {
  const { getTime } = opts;
  const bursts = [];
  let remaining = (rows || []).filter((row) => Number.isFinite(getTime(row)));
  for (;;) {
    const burst = findSlidingWindowBurst(remaining, opts);
    if (!burst) break;
    bursts.push(burst);
    const cutoffT = getTime(burst[burst.length - 1]);
    remaining = remaining.filter((row) => getTime(row) > cutoffT);
  }
  return bursts;
}

// ── Rule 1: mass_void — ≥3 invoice `voided` by the SAME actor within any
// 60-min sliding window. One alert per qualifying, non-overlapping burst
// (an actor can trip this more than once in the lookback window) — each
// carries its OWN event count, not the actor's total voids in the lookback.
export function detectMassVoid(rows) {
  const events = [];
  groupBy(rows, (r) => r.actor_id || 'unknown').forEach((actorRows, actorId) => {
    const bursts = findAllSlidingWindowBursts(actorRows, {
      windowMs: MASS_VOID_WINDOW_MS, minCount: MASS_VOID_MIN_COUNT, getTime: getCreatedAtMs,
    });
    bursts.forEach((burst) => {
      // findAllSlidingWindowBursts returns each burst sorted oldest -> newest.
      const first = burst[0];
      const newest = burst[burst.length - 1];
      events.push({
        // Keyed on the burst's FIRST event so the id stays stable across
        // loads even as later, unrelated events accumulate for this actor —
        // the newest event of a completed, non-overlapping burst never
        // changes once found.
        id: `mass_void:${first.id}`,
        actorId: first.actor_id || null,
        kind: 'mass_void',
        severity: 'high',
        headline: {
          id: 'activity.alerts.massVoid',
          values: { actor: newest.actor_name || null, count: burst.length },
        },
        lastSeen: newest.created_at,
        activity: { entityType: 'invoice', eventType: 'voided' },
      });
    });
  });
  return events;
}

// ── Rule 2: role_escalation — employee `role_changed` whose new role is
// 'admin'. `set_member_role` writes `payload.new_role` (+ `old_role`); `to` is
// read only as a legacy fallback — reading `to` alone never matched a real row,
// so this rule silently never fired. No windowing — every promotion alerts. ──
export function detectRoleEscalation(rows) {
  return (rows || [])
    .filter((row) => (row?.payload?.new_role ?? row?.payload?.to) === 'admin')
    .map((row) => ({
      id: `role_escalation:${row.id}`,
      kind: 'role_escalation',
      severity: 'high',
      actorId: row.actor_id || null,
      // Promotion: entity_id is the promoted user (viewer-relevance filter).
      subjectUserId: row.entity_id || null,
      subjectEmail: row.payload?.email || null,
      headline: {
        id: 'activity.alerts.roleEscalation',
        values: {
          target: row.payload?.employee_name || row.payload?.email || null,
          actor: row.actor_name || null,
        },
      },
      lastSeen: row.created_at,
      // Review-modal role-change line (RoleChangeLine) — same shape the Team
      // "change role" confirm uses. `old_role` only exists on the real
      // set_member_role payload; the legacy `to`-only fallback has no prior
      // role to show, so oldRole is null there.
      details: { oldRole: row.payload?.old_role ?? null, newRole: row.payload?.new_role ?? row.payload?.to ?? null },
      activity: { entityType: 'employee', eventType: 'role_changed' },
    }));
}

// ── Rule 2b: admin invite — employee `invited` with payload.role 'admin'.
// Any admin (not only the Owner) can invite a new admin via invite-user, so
// this — not the Owner-only set_member_role — is the path a stolen admin
// session would use to plant a second admin. Counts as a new admin
// (kind role_escalation) with its own sentence. ──
export function detectAdminInvites(rows) {
  return (rows || [])
    .filter((row) => row?.payload?.role === 'admin')
    .map((row) => ({
      id: `role_escalation:${row.id}`,
      kind: 'role_escalation',
      severity: 'high',
      actorId: row.actor_id || null,
      // Invite: entity_id is the invitee's user id; email is the fallback.
      subjectUserId: row.entity_id || row.payload?.invitee_user_id || null,
      subjectEmail: row.payload?.invitee_email || null,
      headline: {
        id: 'activity.alerts.adminInvited',
        values: {
          target: row.payload?.employee_name || row.payload?.invitee_email || null,
          actor: row.actor_name || null,
        },
      },
      lastSeen: row.created_at,
      // An invite has no PRIOR role to show (RoleChangeLine needs two) — the
      // review-modal card renders "Invited as ● Administrador" instead.
      details: { newRole: 'admin' },
      activity: { entityType: 'employee', eventType: 'invited' },
    }));
}

// ── Rule 3: mfa_disabled — two writers, both count: the admin MFA-reset function
// (entity_type 'employee', the admin-reset edge fn) and `log-auth-security-event`
// (entity_type 'auth', a user turning off their own 2FA). No windowing.
// `_sourceEntity` is a synthetic tag the caller (securityEvents.js) stamps on
// each row BEFORE merging the two queries' results — never a real column —
// so `activity.entityType` can still tell an admin-reset row from a
// self-disable row once they're in one merged array. ──
// The RD-SEC-015 lifecycle sweeper writes self turn-offs as entity_type
// 'auth' with entity_id = actor_id = the user and NO employee_name/email in
// the payload — so when the row is about its own actor, the actor's name IS
// the target (instead of an unnamed fallback).
export function mfaDisabledTarget(row) {
  const selfActor = row?.entity_id && row.entity_id === row.actor_id ? row.actor_name : null;
  return row?.payload?.employee_name || row?.payload?.email || selfActor || null;
}

const MFA_PAIR_WINDOW_MS = 15 * 60 * 1000;

/**
 * One admin/platform 2FA reset writes TWO rows: `employee/mfa_disabled` (the
 * real actor) and ~1 min later `auth/mfa_disabled` from the factor sweeper
 * (actor = entity = the target). Drop the sweeper row when an employee row for
 * the SAME target sits within ±15 min and that row was not self-initiated.
 * Mirrors supabase/functions/_shared/mfaNotificationFold.ts.
 */
export function dropSweeperDuplicates(rows) {
  // ANY employee/mfa_disabled row (incl. initiator 'self') twins the sweeper's
  // auth row for the same target within the window.
  const employeeRows = (rows || []).filter((r) => r?._sourceEntity === 'employee');
  return (rows || []).filter((row) => {
    if (row?._sourceEntity !== 'auth') return true;
    const t = getCreatedAtMs(row);
    return !employeeRows.some((e) => (
      e.entity_id && e.entity_id === row.entity_id
      && Math.abs(getCreatedAtMs(e) - t) <= MFA_PAIR_WINDOW_MS
    ));
  });
}

export function detectMfaDisabled(rows) {
  return dropSweeperDuplicates(rows).map((row) => ({
    id: `mfa_disabled:${row.id}`,
    kind: 'mfa_disabled',
    severity: 'medium',
    actorId: row.actor_id || null,
    // The reset's target: viewers still see 2FA changes about themselves.
    subjectUserId: row.entity_id || null,
    headline: {
      id: 'activity.alerts.mfaDisabled',
      values: {
        target: mfaDisabledTarget(row),
        actor: row.actor_name || null,
      },
    },
    lastSeen: row.created_at,
    activity: { entityType: row._sourceEntity || 'employee', eventType: 'mfa_disabled' },
  }));
}

// ── Rule 4: bulk_catalog_delete — per ACTOR, any 60-min sliding window whose
// total deleted catalog items exceeds 10. Items = sum of bulk_op
// `bulk_delete_catalog_run` rows' deleted_count + 1 per catalog `deleted` row
// (RD-SEC-049: the DB records both server-side, so one-by-one deletes cannot
// evade the rule). Rows carry a synthetic `_sourceEntity` ('bulk_op' |
// 'catalog') stamped by securityEvents.js. One alert per qualifying,
// non-overlapping window; id = first source row (stable, no email/name). ──
function catalogDeleteWeight(row) {
  if (row?._sourceEntity === 'catalog') return 1;
  const n = Number(row?.payload?.deleted_count ?? 0);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function findAllWeightedWindows(rows, { windowMs, minTotal, getTime, getWeight }) {
  const sorted = (rows || [])
    .map((row) => ({ row, t: getTime(row), w: getWeight(row) }))
    .filter(({ t, w }) => Number.isFinite(t) && w > 0)
    .sort((a, b) => a.t - b.t);
  const windows = [];
  let start = 0;
  let sum = 0;
  for (let end = 0; end < sorted.length; end += 1) {
    sum += sorted[end].w;
    while (sorted[end].t - sorted[start].t > windowMs) { sum -= sorted[start].w; start += 1; }
    if (sum >= minTotal) {
      // Absorb the rest of the burst — every row still within windowMs of the
      // window's first row — so 30 deletes in an hour read as ONE alert of 30,
      // not two alerts of 11. The id (first row) is unchanged by absorption.
      let last = end;
      while (last + 1 < sorted.length && sorted[last + 1].t - sorted[start].t <= windowMs) {
        last += 1;
        sum += sorted[last].w;
      }
      windows.push({ rows: sorted.slice(start, last + 1).map((x) => x.row), total: sum });
      end = last;
      start = last + 1; // non-overlapping: next window begins strictly after
      sum = 0;
    }
  }
  return windows;
}

export function detectBulkCatalogDelete(rows) {
  const events = [];
  groupBy(rows, (r) => r.actor_id || 'unknown').forEach((actorRows) => {
    findAllWeightedWindows(actorRows, {
      windowMs: BULK_CATALOG_DELETE_WINDOW_MS,
      minTotal: BULK_CATALOG_DELETE_MIN + 1,
      getTime: getCreatedAtMs,
      getWeight: catalogDeleteWeight,
    }).forEach(({ rows: win, total }) => {
      const first = win[0];
      const newest = win[win.length - 1];
      const hasBulk = win.some((r) => r._sourceEntity !== 'catalog');
      const kinds = new Set(win.map((r) => r.payload?.kind).filter(Boolean));
      events.push({
        id: `bulk_catalog_delete:${first.id}`,
        kind: 'bulk_catalog_delete',
        severity: 'medium',
        actorId: first.actor_id || null,
        headline: {
          id: 'activity.alerts.bulkCatalogDelete',
          values: { actor: newest.actor_name || null, count: total, kind: kinds.size === 1 ? [...kinds][0] : 'item' },
        },
        lastSeen: newest.created_at,
        activity: hasBulk
          ? { entityType: 'bulk_op', eventType: 'bulk_delete_catalog_run' }
          : { entityType: 'catalog', eventType: 'deleted' },
      });
    });
  });
  return events;
}

// ── Rule 5: login_failed_burst — ≥5 `login_failed` for the SAME email within
// any 60-min sliding window. Pre-auth failures carry no actor_id; the target
// email lives in payload.email (falling back to entity_id). One alert per
// qualifying, non-overlapping burst — a repeat attacker over the lookback
// window produces multiple alerts, not just the earliest. ──
export function detectLoginFailedBurst(rows) {
  const events = [];
  groupBy(rows, (r) => String(r.payload?.email || r.entity_id || 'unknown').toLowerCase())
    .forEach((emailRows, email) => {
      const bursts = findAllSlidingWindowBursts(emailRows, {
        windowMs: LOGIN_FAILED_WINDOW_MS, minCount: LOGIN_FAILED_MIN_COUNT, getTime: getCreatedAtMs,
      });
      bursts.forEach((burst) => {
        const first = burst[0];
        const newest = burst[burst.length - 1];
        events.push({
          // No email in the id: it keys a per-user receipt (ids only).
          id: `login_failed_burst:${first.id}`,
          kind: 'login_failed_burst',
          severity: 'high',
          headline: { id: 'activity.alerts.loginFailedBurst', values: { email, count: burst.length } },
          lastSeen: newest.created_at,
          activity: { entityType: 'auth', eventType: 'login_failed' },
        });
      });
    });
  return events;
}

const SEVERITY_RANK = Object.freeze({ high: 0, medium: 1 });

/** Sort high → medium, newest first within each bucket. Mutates + returns. */
export function sortSecurityEvents(events) {
  events.sort((a, b) => {
    const sev = (SEVERITY_RANK[a.severity] ?? 99) - (SEVERITY_RANK[b.severity] ?? 99);
    if (sev !== 0) return sev;
    return (b.lastSeen || '').localeCompare(a.lastSeen || '');
  });
  return events;
}
